import { useEffect, useRef, useState } from 'react'

/** A number for what is arriving, next to the picture of it.
 *
 *  A spectrogram of near-silence is a black rectangle, and a black rectangle is
 *  indistinguishable from a component that is not running. That ambiguity is
 *  expensive: it turns "the sea is quiet tonight" and "live streaming is broken"
 *  into the same screen. So the level is also shown as a number that visibly
 *  moves, and a stream carrying true digital silence says so in words.
 *
 *  RMS and peak in dBFS, because nothing here is calibrated. Converting to
 *  dB re 1 µPa needs all three R2.3 terms and the server decides whether they
 *  are known -- this component never guesses.
 */
export function LevelMeter({ analyser }: { analyser: AnalyserNode }) {
  const [rms, setRms] = useState(-Infinity)
  const [peak, setPeak] = useState(-Infinity)
  const raf = useRef(0)

  useEffect(() => {
    const buf = new Float32Array(analyser.fftSize)
    // Peak decays rather than resetting, so a transient stays readable for a
    // moment instead of flashing past between two animation frames.
    let held = 0
    let holdUntil = 0

    const tick = () => {
      raf.current = requestAnimationFrame(tick)
      analyser.getFloatTimeDomainData(buf)

      let sum = 0
      let mx = 0
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i]!
        sum += v * v
        const a = v < 0 ? -v : v
        if (a > mx) mx = a
      }
      const r = Math.sqrt(sum / buf.length)

      const now = performance.now()
      if (mx >= held || now > holdUntil) {
        held = mx
        holdUntil = now + 900
      }

      const db = (x: number) => (x > 0 ? 20 * Math.log10(x) : -Infinity)
      setRms(db(r))
      setPeak(db(held))
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [analyser])

  // Below this, the samples are zero or near enough that nothing is arriving.
  // A live hydrophone in a quiet fjord still reads well above it.
  const silent = !Number.isFinite(rms) || rms < -110
  const fmt = (v: number) => (Number.isFinite(v) ? `${v.toFixed(1)} dBFS` : '—')

  // -80 dBFS to full scale, which is the range an uncalibrated hydrophone
  // actually occupies. A 0..-∞ bar would sit pinned at the bottom for ever.
  const pos = (v: number) => Math.max(0, Math.min(1, (v + 80) / 80))

  return (
    <div className="meter">
      <div className="meter-bar" role="img"
        aria-label={`Level ${fmt(rms)}, peak ${fmt(peak)}`}>
        <span className="meter-fill" style={{ width: `${pos(rms) * 100}%` }} />
        {Number.isFinite(peak) && (
          <span className="meter-peak" style={{ left: `${pos(peak) * 100}%` }} />
        )}
      </div>
      <div className="meter-read num">
        <span>{fmt(rms)}</span>
        <span className="faint">peak {fmt(peak)}</span>
      </div>
      {silent && (
        <span className="badge" title="The stream is connected but the samples are silent. That is a node problem, not a browser one.">
          receiving silence
        </span>
      )}
    </div>
  )
}
